import { Injectable } from '@angular/core';
import { PostProcessor, PostProcessorType } from '../post-processor';
import { LoggingService } from '../../logging/logic/logging.service';
import { UpdateModelObjectService } from '../../update-model-object/update-model-object.service';
import {
  PostProcessorParameterXML,
  PostProcessorXML,
} from 'src/app/_model/dyn-form/dyn-form-xml';
import { DynForm } from 'src/app/_model/dyn-form';
import { IMSId, IMSObject } from 'src/app/_model/ims-object';
import { ServicejobsService } from '../../servicejobs/logic/servicejobs.service';
import { DeficiencyTicketsService } from '../../deficiency-ticket/logic/deficiency-tickets.service';
import {
  DEFICIENCYTICKET_EXTTYPES,
  DEFICIENCYTICKET_STATUS,
  DeficiencyTicket,
  DeficiencyTicketDTO,
} from 'src/app/_model/deficiency-ticket';
import { ReasonsService } from '../../reasons/logic/reasons.service';
import { AppService } from '../../app-service/logic/app.service';
import { CredentialsService } from '../../credentials/logic/credentials.service';
import { Employee } from 'src/app/_model/employee';
import { EmployeeService } from '../../employee/logic/employee.service';
import { PinsService } from '../../pins/logic/pins.service';
import { AppParameterService } from '../../app-parameter/logic/app-parameter.service';
import { Servicejob } from 'src/app/_model/servicejob';
import { parse } from 'date-fns';
import { RemoteActionUpdateObject } from 'src/app/_model/remote-action/remote-action-builder/remote-action-builder';
import { Pin } from 'src/app/_model/pin';
import { Branch } from 'src/app/_model/branches';
import { BranchesService } from '../../branches/logic/branches.service';

@Injectable({
  providedIn: 'root',
})
export class DeficiencyPostProcessor implements PostProcessor {
  // Public Member
  constructor(
    private deficiencyTicketsService: DeficiencyTicketsService,
    private serviceJobService: ServicejobsService,
    private employeeService: EmployeeService,
    private branchService: BranchesService,
    private pinService: PinsService,
    private appService: AppService,
    private credentialService: CredentialsService,
    private appParamService: AppParameterService,
    private logger: LoggingService,
  ) {}
  public readonly name: PostProcessorType = 'deficiency';

  private readonly PARAMETER_DEFICIENCY_CODE = 'deficiencycode';
  private readonly PARAMETER_TITLE = 'title';
  private readonly PARAMETER_DESCRITION = 'description';
  private readonly PARAMETER_COMPLETION_DATE = 'completiondate';
  private readonly PARAMETER_LATEST_COMPLETION_DATE = 'latestcompletiondate';
  private readonly PARAMETER_IS_EXTERNAL = 'isexternal';
  private readonly PARAMETER_EMP_ASSIGNEE_ID = 'empassigneeid';
  private readonly PARAMETER_BRANCH_ASSIGNEE_ID = 'branchassigneeid';
  private readonly PARAMETER_STATUS = 'status';
  private readonly PARAMETER_DEFICIENCYTICKET_ID = 'deficiencyid';
  private readonly PARAMETER_DEFICIENCYTICKET_LOCAL_ID = 'deficiencylocalid';
  private readonly PARAMETER_PRIORITY = 'priority';

  private readonly PARAMETER_PIN_ID = 'pinId';
  private readonly PARAMETER_SECONDARYPIN_ID = 'secondaryPinId';

  // Lifecycle
  async postProcess(
    form: DynForm,
    formData: any,
    imsObject: IMSObject,
    postProcessorXml: PostProcessorXML,
  ): Promise<RemoteActionUpdateObject[]> {
    this.logger.log(
      this.constructor.name,
      ' postProcess with parameters ->  formData:',
      formData,
      ' postProcessorXml:',
      postProcessorXml,
    );

    if (imsObject.id.objRef != 'ims/ServiceJob') {
      this.logger.warn(
        this.constructor.name,
        'Cannot postprocess deficiency on non ServiceJob owner obj: ',
        imsObject?.id?.objRef,
      );
      return undefined;
    }
    const params =
      postProcessorXml.param instanceof Array
        ? postProcessorXml.param
        : [postProcessorXml.param];
    const paramMap = new Map<string, string>(
      params.map((it) => [it.$.name, it.$.associatedfield]),
    );

    const dtId = formData[paramMap.get(this.PARAMETER_DEFICIENCYTICKET_ID)];
    const dtLocalId =
      formData[paramMap.get(this.PARAMETER_DEFICIENCYTICKET_LOCAL_ID)];

    this.logger.log(
      this.constructor.name,
      `check for deficiencyTicketId ${dtId}(${dtLocalId}) - isCreate=${dtId == undefined && dtLocalId == undefined}`,
    );

    if (dtId == undefined && dtLocalId == undefined) {
      let sjob = this.serviceJobService.getServiceJobById({
        imsId: imsObject.getImsId(),
        secondaryId: undefined,
        objRef: 'ims/ServiceJob',
      });
      if (sjob == undefined) {
        sjob = this.serviceJobService.getServiceJobById({
          imsId: undefined,
          secondaryId: imsObject.getImsId(),
          objRef: 'ims/ServiceJob',
        });
      }
      this.logger.log(this.constructor.name, 'Found sjob', sjob.label);

      const deficiencyTicket = await this.generateDeficiencyTicketFromFormData(
        formData,
        paramMap,
        sjob,
      );
      await this.deficiencyTicketsService.createDeficiencyTicket(
        deficiencyTicket,
      );
      return [
        {
          key: 'entitycreatedlocaldeviceid',
          value: deficiencyTicket.id.secondaryId,
        },
      ];
    } else {
      const id: IMSId = {
        imsId: dtId == '' ? undefined : dtId,
        secondaryId: dtLocalId == '' ? undefined : dtLocalId,
        objRef: 'ims/DeficiencyTicket',
      };
      this.logger.log(
        this.constructor.name,
        'looking for deficiencyTicket with id',
        id,
      );
      let deficiencyTicket =
        this.deficiencyTicketsService.getDeficiencyTicketById(id);

      this.logger.log(
        this.constructor.name,
        'Found deficiencyTicket to update',
        deficiencyTicket,
      );
      deficiencyTicket = await this.updateDeficiencyTicketFromFormData(
        formData,
        paramMap,
        deficiencyTicket,
      );
      this.logger.log(
        this.constructor.name,
        'Update deficiencyTicket with',
        deficiencyTicket,
      );
      await this.deficiencyTicketsService.updateDeficiencyTicket(
        deficiencyTicket,
      );
      return undefined;
    }
  }

  private async updateDeficiencyTicketFromFormData(
    formData: any,
    paramMap: Map<string, string>,
    deficiencyTicket: DeficiencyTicket,
  ): Promise<DeficiencyTicket> {
    const deficiencyCode =
      formData[paramMap.get(this.PARAMETER_DEFICIENCY_CODE)];
    const title = formData[paramMap.get(this.PARAMETER_TITLE)];
    const description = formData[paramMap.get(this.PARAMETER_DESCRITION)];

    const duedate1Str = formData[paramMap.get(this.PARAMETER_COMPLETION_DATE)];
    const duedate2Str =
      formData[paramMap.get(this.PARAMETER_LATEST_COMPLETION_DATE)];
    const empAssigneeId =
      formData[paramMap.get(this.PARAMETER_EMP_ASSIGNEE_ID)];
    const branchAssigneeId =
      formData[paramMap.get(this.PARAMETER_BRANCH_ASSIGNEE_ID)];
    const pinId = formData[paramMap.get(this.PARAMETER_PIN_ID)];
    const isExternal = formData[paramMap.get(this.PARAMETER_IS_EXTERNAL)];
    const status = formData[paramMap.get(this.PARAMETER_STATUS)];
    const priority = formData[paramMap.get(this.PARAMETER_PRIORITY)];

    this.logger.log(
      this.constructor.name,
      'Update Deficiency ',
      deficiencyCode?.label,
    );

    let empAssignee: Employee;
    if (empAssigneeId != undefined) {
      empAssignee = this.employeeService.getEmployeeById({
        imsId: +empAssigneeId,
        secondaryId: undefined,
        objRef: 'ims/Employee',
      });
    }
    let pin = this.pinService.getPinById({
      imsId: +pinId,
      secondaryId: undefined,
      objRef: 'ims/Pin',
    });
    if (pin == undefined) {
      pin = this.pinService.getPinById({
        imsId: undefined,
        secondaryId: pinId,
        objRef: 'ims/Pin',
      });
    }

    deficiencyTicket.extCode = deficiencyCode;
    deficiencyTicket.title = title;
    deficiencyTicket.description = description;
    console.log(
      this.constructor,
      'update with cdate',
      duedate1Str,
      'orig',
      deficiencyTicket.completionDate,
      deficiencyTicket.completionDateStr,
    );
    console.log(
      this.constructor,
      'update with lcdate',
      duedate2Str,
      'orig',
      deficiencyTicket.latestCompletionDate,
      deficiencyTicket.latestCompletionDateStr,
    );
    (deficiencyTicket.completionDate =
      duedate1Str != undefined && duedate1Str != ''
        ? parse(duedate1Str, 'dd.MM.yyyy', new Date())?.getTime()
        : undefined),
      (deficiencyTicket.latestCompletionDate =
        duedate2Str != undefined && duedate2Str != ''
          ? parse(duedate2Str, 'dd.MM.yyyy', new Date())?.getTime()
          : undefined),
      (deficiencyTicket.external = isExternal);
    deficiencyTicket.assigneeObjRef =
      empAssigneeId != undefined
        ? 'ims/Employee'
        : branchAssigneeId != undefined
          ? 'ims/Branch'
          : undefined;
    deficiencyTicket.assignee =
      empAssigneeId != undefined
        ? empAssignee
        : branchAssigneeId != undefined
          ? undefined
          : undefined;
    deficiencyTicket.status = status;
    deficiencyTicket.priority = +priority;

    return deficiencyTicket;
  }

  private async generateDeficiencyTicketFromFormData(
    formData: any,
    paramMap: Map<string, string>,
    sjob: Servicejob,
  ): Promise<DeficiencyTicket> {
    const deficiencyCode =
      formData[paramMap.get(this.PARAMETER_DEFICIENCY_CODE)];
    const title = formData[paramMap.get(this.PARAMETER_TITLE)];
    const description = formData[paramMap.get(this.PARAMETER_DESCRITION)];

    const duedate1Str = formData[paramMap.get(this.PARAMETER_COMPLETION_DATE)];
    const duedate2Str =
      formData[paramMap.get(this.PARAMETER_LATEST_COMPLETION_DATE)];
    const empAssigneeId =
      formData[paramMap.get(this.PARAMETER_EMP_ASSIGNEE_ID)];
    const branchAssigneeId =
      formData[paramMap.get(this.PARAMETER_BRANCH_ASSIGNEE_ID)];
    const isExternal = formData[paramMap.get(this.PARAMETER_IS_EXTERNAL)];
    const priority = formData[paramMap.get(this.PARAMETER_PRIORITY)];

    this.logger.log(
      this.constructor.name,
      'Create Deficiency with code',
      deficiencyCode,
      'for sjob',
      sjob.getImsId(),
      'with',
      title,
      description,
    );

    let empAssignee: Employee;
    if (empAssigneeId != undefined) {
      empAssignee = this.employeeService.getEmployeeById({
        imsId: +empAssigneeId,
        secondaryId: undefined,
        objRef: 'ims/Employee',
      });
    }
    let branchAssignee: Branch;
    if (branchAssigneeId != undefined) {
      branchAssignee = this.branchService.getBranchById({
        imsId: +branchAssigneeId,
        secondaryId: undefined,
        objRef: 'ims/Branch',
      });
    }
    console.log(
      this.constructor.name,
      'empassignid',
      empAssigneeId,
      'branchassignid',
      branchAssigneeId,
    );
    const pinId = formData[this.PARAMETER_PIN_ID];
    const pinSecondaryId = formData[this.PARAMETER_SECONDARYPIN_ID];
    let pin: Pin;
    if (pinId) {
      pin = this.pinService.getPinById({
        imsId: +pinId,
        secondaryId: undefined,
        objRef: 'ims/Pin',
      });
    }

    if (pin == undefined && pinSecondaryId) {
      pin = this.pinService.getPinById({
        imsId: undefined,
        secondaryId: pinSecondaryId,
        objRef: 'ims/Pin',
      });
    }
    console.log('Found Pin', pin, pinId, pinSecondaryId, formData);

    const me = this.employeeService.me$.getValue();

    const deficiencyTicketDto: DeficiencyTicketDTO = {
      ownerobjref: 'ims/ServiceJob',
      extcode: deficiencyCode,
      exttype: DEFICIENCYTICKET_EXTTYPES.DFY,
      owner: {
        id: sjob?.getImsId(),
        label: sjob?.label,
      },
      label: title,
      title: title,
      description: description,
      entitycreatedtstamp: Date.now(),
      entitycreateddevicetoken: +(await this.appService.getTokenId()),
      entitycreateduser:
        this.credentialService.credentials$.getValue().username,
      status: DEFICIENCYTICKET_STATUS.STATUS_OPEN,
      priority: +priority,
      completiondate:
        duedate1Str != undefined
          ? parse(duedate1Str, 'dd.MM.yyyy', new Date())?.getTime()
          : undefined,
      latestcompletiondate:
        duedate2Str != undefined
          ? parse(duedate2Str, 'dd.MM.yyyy', new Date())?.getTime()
          : undefined,
      external: isExternal,
      assigneeobjref: isExternal ? 'ims/Branch' : 'ims/Employee',
      assignee: {
        id: isExternal ? branchAssigneeId : empAssigneeId,
        label: isExternal ? branchAssignee?.label : empAssignee?.label,
      },
      creator: {
        id: me?.getImsId(),
        label: me?.label,
      },
    };

    console.log(this.constructor.name, 'generated DTO:', deficiencyTicketDto);
    const tt = DeficiencyTicket.fromDto(
      deficiencyTicketDto,
      undefined,
      deficiencyTicketDto,
      this.appParamService.getFormatLabelForObject('ims/DeficiencyTicket'),
    );
    if (pin) {
      tt.pin = pin;
    }
    console.log(this.constructor.name, 'generated TT:', tt);
    return tt;
  }
}
